import { useQuery } from '@tanstack/react-query';
import { intelligenceRecords } from '@cwos/api-client';
import { Pill } from '@cwos/ui-admin';
import { authorityGate, gateOpen, gateRefused } from '../../authorityGate.js';
import { usePermissions } from '../../permissions.js';
import { listPhase } from '../../listState.js';
import { ListState } from '../../ListStates.js';
import { periodLine, reachLine, withheldLine, windowLine } from './recordsLogic.js';

/** The literal `learning/routes.ts` guards all three learning routes with. */
export const LEARNING_READ_KEY = 'learning:read';

/**
 * WHAT WE LEARNED, AND FROM WHAT — the other half of the advice loop.
 *
 * It is a SECTION of the Recommendations screen rather than a rail entry of its
 * own, because a learning signal is an aggregate over outcome records that come
 * from these recommendations: putting it behind a second navigation would ask a
 * reader to know that before they could find it.
 *
 * ---------------------------------------------------------------------------
 * IT GATES ITSELF, BECAUSE THE PAGE'S KEY IS NOT THIS SECTION'S KEY
 * ---------------------------------------------------------------------------
 * The route charges `recommendation:read`. This section charges
 * `learning:read`, and the two are held independently — 1839001000000 granted
 * the learning read to org_admin, supervisor and analyst, and a tenant can
 * revoke either. So the query is `enabled` only on a RESOLVED refusal-free
 * gate, and a refused reader is told what the section is rather than shown a
 * hole. "Not yet known" is not "not held": `authorityGate` distinguishes them
 * and only a settled `refused` produces the sentence.
 *
 * ---------------------------------------------------------------------------
 * `catalogLoaded: false` IS A REFUSAL THAT SAYS SO, NOT AN EMPTY MEASUREMENT
 * ---------------------------------------------------------------------------
 * The list route answers 200 with all-zero counts when the permission catalog
 * cannot be resolved, because a gate that cannot resolve keys must refuse and
 * the answer must SAY it refused. Rendering that as "no learning yet" would
 * report an outage as an organizational fact, so it has its own branch and it
 * comes before the empty state.
 */
export function LearningSignals() {
  const permissions = usePermissions();
  const gate = authorityGate(permissions, LEARNING_READ_KEY);
  const refused = gateRefused(gate);

  const list = useQuery({
    queryKey: ['intelligence', 'learning', 'signals'],
    queryFn: () => intelligenceRecords.listLearningSignals(),
    // Default-deny for a REQUEST is right: an unheld key means the server would
    // 403, and firing it anyway teaches a reader nothing. The SENTENCE below is
    // claimed only from a settled refusal, which is the other half of the rule.
    enabled: gateOpen(gate),
  });

  const signals = list.data?.signals ?? [];
  const withheld = withheldLine(list.data?.withheld, 'signal');
  const window = list.data ? windowLine(list.data.scanned, list.data.windowExhausted) : null;
  const catalogRefused = list.data?.catalogLoaded === false;

  return (
    <section className="rn-col" aria-labelledby="learning-signals-heading">
      <h2 id="learning-signals-heading">What we learned</h2>
      <p className="rn-muted">
        Organization-level readings derived from the outcomes recorded above. A signal carries the
        permissions of the records it was derived from, so aggregating does not widen who may read
        it.
      </p>

      {refused ? (
        <p className="rn-muted" role="status">
          Learning signals are not shown to you. They are derived from outcome records, and reading
          them needs <code>learning:read</code>. This is not a statement that your organization has
          filed none.
        </p>
      ) : catalogRefused ? (
        <p className="rn-muted" role="status">
          The permission catalog could not be resolved, so learning reads failed closed. This says
          nothing about whether your organization has filed any signals.
        </p>
      ) : (
        <>
          {withheld ? <p className="rn-muted">{withheld}</p> : null}
          {window ? <p className="rn-muted">{window}</p> : null}
          <ListState
            phase={listPhase(list, signals.length)}
            surface="band"
            rows={3}
            failedMessage="We could not read the learning signals. Nothing has changed."
            onRetry={() => void list.refetch()}
            empty={
              <p className="rn-muted">
                No learning signal has been filed for your organization. Filing one is a deliberate
                act at the end of a measurement period, so none is the ordinary state.
              </p>
            }
          >
            <ul className="rn-col" style={{ listStyle: 'none', padding: 0 }}>
              {signals.map((signal) => (
                <li key={signal.id} className="rn-col" style={{ gap: 'var(--space-8)' }}>
                  <span className="rn-strong">
                    {signal.kind} · {signal.direction}
                  </span>
                  <span className="rn-muted">{periodLine(signal.period)}</span>
                  {signal.payload?.note ? (
                    <span className="rn-muted">{signal.payload.note}</span>
                  ) : null}
                  <span className="rn-muted">
                    Derived from {signal.derivedFrom.outcomeRefs.length} outcome record
                    {signal.derivedFrom.outcomeRefs.length === 1 ? '' : 's'}.
                  </span>
                  {/* The audience is the property that makes the read key safe,
                      so it is shown rather than assumed. */}
                  <span className="rn-muted">{reachLine(signal.permissionRequirements)}</span>
                  <Pill variant="plain">confidence {Math.round(signal.confidence * 100)}%</Pill>
                </li>
              ))}
            </ul>
          </ListState>
        </>
      )}
    </section>
  );
}
